import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { LastMetric, LastMetricByDatabase, Metric, PostMetric, PutMetric } from 'src/app/interfaces/api/metric';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root',
})
export class MetricService {

  collection = "Metric";
  collectionPath = "metrics";
  metrics: Metric[] = [];
  apiUrl = environment.API_URL;

  constructor(
    private apiService: ApiService,
    public http: HttpClient,
  ) {
    // this.load()
  }

  //---GET---//

  getMetrics(query: QueryParams = {}): Observable<Metric[]> {
    return this.apiService.getCollection(this.collectionPath, "", query).pipe(
      tap((res: any) => {
        if (res) {
          localStorage.setItem('metrics', res);
        }
      }),
      catchError((err) => throwError(err))
    );
  }
  getLastMetrics(database: string = ""): Observable<LastMetric[]> {
    return this.apiService.getCollection(this.collectionPath, 'last-metric', { database });
  }

  getLastMetricsByDatabase(databases: string[]): Observable<LastMetricByDatabase[]> {
    return forkJoin(
      databases.map(database => this.getLastMetrics(database)
        .pipe(map(metrics => ({ database, metrics })))
      )
    )
  }

  getMetric(id: string): Observable<Metric> {
    return this.apiService.getCollectionItem(this.collectionPath, "", id);
  }

  getPlanningMetrics(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'planning', query)
  }

  searchMetrics(search: string, query: QueryParams = {}): Observable<Metric[]> {
    return this.apiService.getCollection(this.collectionPath, 'search', { q: search, ...query });
  }

  exportMetrics(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'export', query);
  }

  getTimelineData(query: QueryParams = {}): Observable<any> {
    let routes = [
      this.apiService.getCollection(this.collectionPath, "", query),
      this.apiService.getCollection("events", "", query),
      this.apiService.postCollectionData("attachments", "getAttachments", query),
    ]
    return forkJoin(routes);
  }

  getMetricsCount(query: QueryParams = {}): Observable<number> {
    return this.apiService.getCollection(this.collectionPath, 'count', query);
  }

  getMetricNames(): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'names');
  }

  getValueList(field: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'listValues', { field });
  }

  getAverageRisks(query: { startDate?: string; endDate?: string; name?: string } = {}): Observable<any[]> {
    const db = sessionStorage.getItem("db") || localStorage.getItem("db") || "default";
    const endDateKey = query.endDate ? format(new Date(query.endDate), 'dd-MM-yyyy') : '';
    const cacheKey = `metrics_average_${db}_${endDateKey}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, expiry } = JSON.parse(cached);
        if (Date.now() < expiry) return of(data);
      } catch {}
    }
    return this.apiService.getCollection(this.collectionPath, 'average', query).pipe(
      tap(data => {
        console.log('----> data :>>', data);
        localStorage.setItem(cacheKey, JSON.stringify({ data, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
      })
    );
  }

  //---POST---//

  addMetric(metric: PostMetric): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, "", metric);
  }

  addMetrics(metrics: PostMetric[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/metrics/many`, metrics);
  }

  reportRisk(metric: Metric): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, "reportRisk", metric);
  }

  uploadMetrics(metrics: any, options: { type: string | Blob; }): Observable<any> {
    let data = new FormData();
    data.append('metrics', metrics);
    data.append('type', options.type);
    return this.http.post(`${this.apiUrl}/metrics/import`, data, { reportProgress: true, observe: 'events' });
  }

  //---PUT---//

  setMetric(metric: PutMetric): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, "", metric);
  }

  //---DELETE---//

  removeMetric(id: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, id);
  }

}
